import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Boxes, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";

const WAREHOUSE_IMG =
  "https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBzaGVsdmVzfGVufDB8fHx8MTc4Nzk2MTc5NHww&ixlib=rb-4.1.0&q=85";

const DEMO = [
  { email: "admin@jcsupplys.com", password: "Admin123!", role: "Owner" },
  { email: "installer@jcsupplys.com", password: "Install123!", role: "Installation Manager" },
  { email: "staff@jcsupplys.com", password: "Staff123!", role: "Warehouse Staff" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@jcsupplys.com");
  const [password, setPassword] = useState("Admin123!");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (u) => {
    setEmail(u.email);
    setPassword(u.password);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      {/* Left panel: brand + image */}
      <div className="hidden lg:block lg:col-span-3 relative bg-slate-900 text-white">
        <img
          src={WAREHOUSE_IMG}
          alt="Warehouse shelves"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/80 via-slate-950/60 to-slate-900/40" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <Logo variant="dark" size="lg" />
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-blue-200 mb-4">
              <Boxes size={14} /> Operations Console
            </div>
            <h2 className="font-display text-5xl font-black tracking-tighter leading-[1.05]">
              Move stock. Ship projects.
              <span className="block text-blue-300">Never lose a record.</span>
            </h2>
            <p className="mt-4 text-slate-300 max-w-md">
              Every receive, transfer, archive, and delete is auditable — physical warehouse
              counts stay honest, even when projects wind down.
            </p>
          </div>
          <div className="text-xs font-mono text-slate-400">
            © {new Date().getFullYear()} Just Compare · Educational & Office Supply
          </div>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo variant="light" />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-blue-800 mb-2">
            Sign in
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">
            Ops Console access
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Use one of the demo accounts below or your assigned credentials.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-blue-800 focus-visible:border-blue-800"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 rounded-sm h-11 border-slate-300 focus-visible:ring-blue-800 focus-visible:border-blue-800"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
              Sign in
            </Button>
          </form>

          <div className="mt-8">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Demo accounts
            </div>
            <div className="space-y-2">
              {DEMO.map((u) => (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => quickFill(u)}
                  data-testid={`demo-${u.role.toLowerCase().replace(/\s/g, "-")}-btn`}
                  className="w-full text-left border border-slate-200 hover:border-blue-800 hover:bg-blue-50 rounded-sm p-3 transition-colors"
                >
                  <div className="text-sm font-semibold text-slate-900">{u.role}</div>
                  <div className="text-xs font-mono text-slate-500">
                    {u.email} · {u.password}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
